import { Uuid, getUuid } from '@sibe/utilities/common';
import type { HsHypersceneTypes } from '../../../math-new/types/hyperscene-types.type';
import { HsGraphicConstants } from '../../constants/graphic-constants.namespace';
import type { HsShaderObjectType } from '../enums/shader-object-type.enum';
import { hsCompileShaderProgram } from '../functions/compile-shader-program.function';
import type { HsGraphicsBuffer } from '../../types/graphic-buffer.type';

/**
 * Vertex attribute array state of a context's default vertex array object, shared by every shader on that context.
 * Bit N stands for attribute location N; WebGL implementations expose at most 16 or 32 locations.
 */
interface VertexAttributeState {
  enabled: number;
  instanced: number;
}

const vertexAttributeStates: WeakMap<WebGL2RenderingContext, VertexAttributeState> = new WeakMap();

function getVertexAttributeState(gl: WebGL2RenderingContext): VertexAttributeState {
  let state: VertexAttributeState | undefined = vertexAttributeStates.get(gl);
  if (state === undefined) {
    state = { enabled: 0, instanced: 0 };
    vertexAttributeStates.set(gl, state);
  }
  return state;
}

function resetInstancedLocations(gl: WebGL2RenderingContext, state: VertexAttributeState): void {
  let instanced: number = state.instanced;
  while (instanced !== 0) {
    const location: number = 31 - Math.clz32(instanced);
    gl.vertexAttribDivisor(location, 0);
    instanced &= ~(1 << location);
  }
  state.instanced = 0;
}

/**
 * Handles s shader.
 */
export abstract class HsShader {
  protected attributes: Map<string, number> = new Map<string, number>();
  protected uniforms: Map<string, WebGLUniformLocation> = new Map<string, WebGLUniformLocation>();

  protected program: WebGLProgram | null = null;
  private completionStatus: number | null = null;
  private attributeMaskSource: Map<string, number> | null = null;
  private attributeMaskSize: number = 0;
  private attributeMaskValue: number = 0;
  private attributeLayoutValue: string = '';

  public uuid: Uuid = getUuid();
  public hasTransparency: boolean = false;
  /** Camera uniform revision last uploaded to this program by HsRender; reset whenever the program is linked. */
  public cameraUniformsRevision: number = -1;

  private readonly uniform1Cache: Map<string, number> = new Map<string, number>();
  // Cached vectors are updated in place so that changing a uniform does not allocate.
  private readonly uniform2Cache: Map<string, HsHypersceneTypes.Array2> = new Map<string, HsHypersceneTypes.Array2>();
  private readonly uniform3Cache: Map<string, HsHypersceneTypes.Array3> = new Map<string, HsHypersceneTypes.Array3>();
  private readonly matrix4UniformValues: Float32Array = new Float32Array(16);

  constructor(public readonly shaderType: HsShaderObjectType) {}

  public makeVertexCode: () => string = () => '';
  public makeFragmentCode: () => string = () => '';

  public update(gl: WebGL2RenderingContext): void {
    this.completionStatus = null;
    this.program = hsCompileShaderProgram(gl, this.makeVertexCode(), this.makeFragmentCode());
    gl.useProgram(this.program);
    this.findAndSetAttributes(gl);
    this.findAndSetUniforms(gl);
  }

  public prepare(gl: WebGL2RenderingContext): void {
    this.deleteProgram(gl);
    const extension: { COMPLETION_STATUS_KHR: number } | null = gl.getExtension('KHR_parallel_shader_compile');
    if (extension === null || !Number.isFinite(extension.COMPLETION_STATUS_KHR)) {
      this.update(gl);
      return;
    }
    this.program = hsCompileShaderProgram(gl, this.makeVertexCode(), this.makeFragmentCode(), true);
    this.completionStatus = extension.COMPLETION_STATUS_KHR;
  }

  public isReady(gl: WebGL2RenderingContext, wait: boolean = false): boolean {
    if (this.completionStatus === null) {
      return this.program !== null;
    }
    if (this.program === null || (!wait && !Boolean(gl.getProgramParameter(this.program, this.completionStatus)))) {
      return false;
    }
    if (!Boolean(gl.getProgramParameter(this.program, gl.LINK_STATUS))) {
      const message: string = `ERROR: compileShaderProgram - Program link error: ${gl.getProgramInfoLog(this.program)}`;
      this.deleteProgram(gl);
      throw new Error(message);
    }
    this.findAndSetAttributes(gl);
    this.findAndSetUniforms(gl);
    this.completionStatus = null;
    return true;
  }

  public useProgram(gl: WebGL2RenderingContext): void {
    gl.useProgram(this.program);
  }

  public deleteProgram(gl: WebGL2RenderingContext): void {
    if (this.program !== null) {
      gl.deleteProgram(this.program);
      this.program = null;
    }
    this.attributes.clear();
    this.uniforms.clear();
    this.cameraUniformsRevision = -1;
    this.uniform1Cache.clear();
    this.uniform2Cache.clear();
    this.uniform3Cache.clear();
    this.completionStatus = null;
  }

  public getAttribute(name: string): number {
    const attributeLocation: number | undefined = this.attributes.get(name);
    if (attributeLocation === undefined) {
      return -1;
    }
    return attributeLocation;
  }

  public uniform1i(gl: WebGL2RenderingContext, name: string, value: number): void {
    if (value === this.uniform1Cache.get(name)) {
      return;
    }
    const uniformLocation: WebGLUniformLocation | undefined = this.uniforms.get(name);
    if (uniformLocation === undefined) {
      return;
    }
    gl.uniform1i(uniformLocation, value);
    this.uniform1Cache.set(name, value);
  }

  public uniform1f(gl: WebGL2RenderingContext, name: string, value: number): void {
    if (value === this.uniform1Cache.get(name)) {
      return;
    }
    const uniformLocation: WebGLUniformLocation | undefined = this.uniforms.get(name);
    if (uniformLocation === undefined) {
      return;
    }
    gl.uniform1f(uniformLocation, value);
    this.uniform1Cache.set(name, value);
  }

  public uniform2f(gl: WebGL2RenderingContext, name: string, x: number, y: number): void {
    const value: HsHypersceneTypes.Array2 | undefined = this.uniform2Cache.get(name);
    if (value !== undefined && x === value[0] && y === value[1]) {
      return;
    }
    const uniformLocation: WebGLUniformLocation | undefined = this.uniforms.get(name);
    if (uniformLocation === undefined) {
      return;
    }
    gl.uniform2f(uniformLocation, x, y);
    if (value === undefined) {
      this.uniform2Cache.set(name, [x, y]);
    } else {
      value[0] = x;
      value[1] = y;
    }
  }

  public uniform2i(gl: WebGL2RenderingContext, name: string, x: number, y: number): void {
    const uniformLocation: WebGLUniformLocation | undefined = this.uniforms.get(name);
    if (uniformLocation === undefined) {
      return;
    }
    gl.uniform2i(uniformLocation, x, y);
  }

  public uniform2fv(gl: WebGL2RenderingContext, name: string, values: HsHypersceneTypes.Array2): void {
    const uniformLocation: WebGLUniformLocation | undefined = this.uniforms.get(name);
    if (uniformLocation === undefined) {
      return;
    }
    gl.uniform2fv(uniformLocation, values);
  }

  public uniform3f(gl: WebGL2RenderingContext, name: string, x: number, y: number, z: number): void {
    const value: HsHypersceneTypes.Array3 | undefined = this.uniform3Cache.get(name);
    if (value !== undefined && x === value[0] && y === value[1] && z === value[2]) {
      return;
    }
    const uniformLocation: WebGLUniformLocation | undefined = this.uniforms.get(name);
    if (uniformLocation === undefined) {
      return;
    }
    gl.uniform3f(uniformLocation, x, y, z);
    if (value === undefined) {
      this.uniform3Cache.set(name, [x, y, z]);
    } else {
      value[0] = x;
      value[1] = y;
      value[2] = z;
    }
  }

  public uniform3i(gl: WebGL2RenderingContext, name: string, x: number, y: number, z: number): void {
    const uniformLocation: WebGLUniformLocation | undefined = this.uniforms.get(name);
    if (uniformLocation === undefined) {
      return;
    }
    gl.uniform3i(uniformLocation, x, y, z);
  }

  public uniform3fv(gl: WebGL2RenderingContext, name: string, values: HsHypersceneTypes.Array3): void {
    const uniformLocation: WebGLUniformLocation | undefined = this.uniforms.get(name);
    if (uniformLocation === undefined) {
      return;
    }
    gl.uniform3fv(uniformLocation, values);
  }

  public uniform4f(gl: WebGL2RenderingContext, name: string, x: number, y: number, z: number, w: number): void {
    const uniformLocation: WebGLUniformLocation | undefined = this.uniforms.get(name);
    if (uniformLocation === undefined) {
      return;
    }
    gl.uniform4f(uniformLocation, x, y, z, w);
  }

  public uniform4i(gl: WebGL2RenderingContext, name: string, x: number, y: number, z: number, w: number): void {
    const uniformLocation: WebGLUniformLocation | undefined = this.uniforms.get(name);
    if (uniformLocation === undefined) {
      return;
    }
    gl.uniform4i(uniformLocation, x, y, z, w);
  }

  public uniform4fv(gl: WebGL2RenderingContext, name: string, values: HsHypersceneTypes.Array4 | Float32Array): void {
    const uniformLocation: WebGLUniformLocation | undefined = this.uniforms.get(name);
    if (uniformLocation === undefined) {
      return;
    }
    gl.uniform4fv(uniformLocation, values);
  }

  public uniformMatrix2fv(gl: WebGL2RenderingContext, name: string, values: HsHypersceneTypes.Array4): void {
    const uniformLocation: WebGLUniformLocation | undefined = this.uniforms.get(name);
    if (uniformLocation === undefined) {
      return;
    }
    gl.uniformMatrix2fv(uniformLocation, false, values);
  }

  public uniformMatrix3fv(gl: WebGL2RenderingContext, name: string, values: HsHypersceneTypes.Array9): void {
    const uniformLocation: WebGLUniformLocation | undefined = this.uniforms.get(name);
    if (uniformLocation === undefined) {
      return;
    }
    gl.uniformMatrix3fv(uniformLocation, false, values);
  }

  public uniformMatrix4fv(gl: WebGL2RenderingContext, name: string, values: HsHypersceneTypes.Array16): void {
    const uniformLocation: WebGLUniformLocation | undefined = this.uniforms.get(name);
    if (uniformLocation === undefined) {
      return;
    }
    this.matrix4UniformValues.set(values);
    gl.uniformMatrix4fv(uniformLocation, false, this.matrix4UniformValues);
  }

  public selectInstanceMatrix4(gl: WebGL2RenderingContext, name: string, buffer: HsGraphicsBuffer | null): void {
    const bytesPerVec: number = HsGraphicConstants.FLOAT32_BYTES * 4;
    const bytesPerMatrix: number = bytesPerVec * 4;
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    for (let i: number = 0; i < 4; i++) {
      const offset: number = i * bytesPerVec;
      const location: number = this.getAttribute(`${name}${i}`);
      if (location === -1) {
        continue;
      }
      gl.vertexAttribPointer(location, 4, gl.FLOAT, false, bytesPerMatrix, offset);
      this.setAttributeInstanced(gl, location, true);
    }
  }

  public deselectInstanceMatrix4(gl: WebGL2RenderingContext, name: string): void {
    for (let i: number = 0; i < 4; i++) {
      const location: number = this.getAttribute(`${name}${i}`);
      if (location === -1) {
        continue;
      }
      this.setAttributeInstanced(gl, location, false);
    }
  }

  /** Sets the attribute divisor to 1 or 0, skipping the call when the location already advances that way. */
  public setAttributeInstanced(gl: WebGL2RenderingContext, location: number, instanced: boolean): void {
    const state: VertexAttributeState = getVertexAttributeState(gl);
    const bit: number = 1 << location;
    if (((state.instanced & bit) !== 0) === instanced) {
      return;
    }
    gl.vertexAttribDivisor(location, instanced ? 1 : 0);
    state.instanced ^= bit;
  }

  /** Restores per-vertex stepping on every location that a previous instanced draw left with a divisor. */
  public resetInstancedAttributes(gl: WebGL2RenderingContext): void {
    resetInstancedLocations(gl, getVertexAttributeState(gl));
  }

  /**
   * Enables exactly this shader's attribute arrays. Only locations whose state differs are touched, so switching
   * between programs that share attribute locations costs no enable or disable calls.
   */
  public enable(gl: WebGL2RenderingContext): void {
    const state: VertexAttributeState = getVertexAttributeState(gl);
    const mask: number = this.attributeMask;
    let changed: number = state.enabled ^ mask;
    while (changed !== 0) {
      const location: number = 31 - Math.clz32(changed);
      const bit: number = 1 << location;
      if ((mask & bit) !== 0) {
        gl.enableVertexAttribArray(location);
      } else {
        gl.disableVertexAttribArray(location);
      }
      changed &= ~bit;
    }
    state.enabled = mask;
    this.resetInstancedAttributes(gl);
  }

  public disable(gl: WebGL2RenderingContext): void {
    const state: VertexAttributeState = getVertexAttributeState(gl);
    const mask: number = this.attributeMask;
    let enabled: number = state.enabled & mask;
    while (enabled !== 0) {
      const location: number = 31 - Math.clz32(enabled);
      gl.disableVertexAttribArray(location);
      enabled &= ~(1 << location);
    }
    state.enabled &= ~mask;
  }

  public get attributeCount(): number {
    return this.attributes.size;
  }

  /** Attribute names and locations; programs with equal layouts can share vertex array objects. */
  public get attributeLayout(): string {
    this.updateAttributeMetadata();
    return this.attributeLayoutValue;
  }

  /** Bit set of this shader's attribute locations. */
  private get attributeMask(): number {
    this.updateAttributeMetadata();
    return this.attributeMaskValue;
  }

  /** Rebuilds derived attribute data only when the attribute map changes. */
  private updateAttributeMetadata(): void {
    if (this.attributeMaskSource === this.attributes && this.attributeMaskSize === this.attributes.size) {
      return;
    }
    let mask: number = 0;
    const entries: string[] = [];
    this.attributes.forEach((location: number, name: string) => {
      mask |= 1 << location;
      entries.push(`${name}:${location}`);
    });
    this.attributeMaskSource = this.attributes;
    this.attributeMaskSize = this.attributes.size;
    this.attributeMaskValue = mask;
    this.attributeLayoutValue = entries.sort().join(',');
  }

  private findAndSetAttributes(gl: WebGL2RenderingContext): void {
    if (this.program === null) {
      return;
    }
    const numAttributes: number = gl.getProgramParameter(this.program, gl.ACTIVE_ATTRIBUTES);
    this.attributes = new Map<string, number>();
    for (let i: number = 0; i < numAttributes; i++) {
      const attributeInfo: WebGLActiveInfo | null = gl.getActiveAttrib(this.program, i);
      if (attributeInfo === null) {
        continue;
      }
      const attributeLocation: number = gl.getAttribLocation(this.program, attributeInfo.name);
      if (attributeLocation === -1) {
        continue;
      }
      this.attributes.set(attributeInfo.name, attributeLocation);
    }
  }

  private findAndSetUniforms(gl: WebGL2RenderingContext): void {
    if (this.program === null) {
      return;
    }
    const numUniforms: number = gl.getProgramParameter(this.program, gl.ACTIVE_UNIFORMS);
    this.uniforms = new Map<string, WebGLUniformLocation>();
    this.cameraUniformsRevision = -1;
    for (let i: number = 0; i < numUniforms; i++) {
      const uniformInfo: WebGLActiveInfo | null = gl.getActiveUniform(this.program, i);
      if (uniformInfo === null) {
        continue;
      }
      const uniformLocation: WebGLUniformLocation | null = gl.getUniformLocation(this.program, uniformInfo.name);
      if (uniformLocation === null) {
        continue;
      }
      this.uniforms.set(uniformInfo.name, uniformLocation);
    }
  }

  /** Disables every attribute array and instance divisor that a shader left on the default vertex array. */
  public static disableAllAttributes(gl: WebGL2RenderingContext): void {
    const state: VertexAttributeState = getVertexAttributeState(gl);
    let enabled: number = state.enabled;
    while (enabled !== 0) {
      const location: number = 31 - Math.clz32(enabled);
      gl.disableVertexAttribArray(location);
      enabled &= ~(1 << location);
    }
    state.enabled = 0;
    resetInstancedLocations(gl, state);
  }
}
